function decorateImgUrl(url, width) {
  return width
    ? url.includes(`width=${width}`)
      ? url
      : url + `&width=${width}`
    : url;
}

/**
 * ProgressiveVariantPicker
 * Handles inline swatch selection and progressive, step-by-step variant picking modal.
 * Focuses on readability and small robustness improvements without changing behavior.
 */
class ProgressiveVariantPicker {
     static INSTANCES = [];

     /**
      * Detect if the given element has any ancestor with a CSS transform applied.
      * Fixed-position children can behave unexpectedly inside transformed ancestors.
      * @param {HTMLElement} el
      * @returns {boolean}
      */
     static hasTransformAncestor(el) {
          let parent = el;
          while (parent && parent !== document.body) {
               const style = window.getComputedStyle(parent);
               if (style.transform && style.transform !== "none") {
                    return true;
               }
               parent = parent.parentElement;
          }
          return false;
     }

     /**
      * Determine whether the viewport should be treated as mobile for modal placement.
      * Uses matchMedia if available, otherwise falls back to window heuristics.
      * @returns {boolean}
      */
     static isMobileViewport() {
          try {
               const mq767 = window.matchMedia("(max-width: 767px)");
               const mq1024Portrait = window.matchMedia("(max-width: 1024px) and (orientation: portrait)");
               return mq767.matches || mq1024Portrait.matches;
          } catch (e) {
               const portrait = typeof window.orientation === "number"
                    ? window.orientation === 0 || window.orientation === 180
                    : window.innerHeight >= window.innerWidth;
               return window.innerWidth <= 767 || (window.innerWidth <= 1024 && portrait);
          }
     }
     constructor(productCard,add_btn,successCallback,skipUnavailable = true) {
          if (!productCard) return;
          this.skipUnavailable = skipUnavailable;
          this.productCard = productCard;
          this.localeData = window._erj_?.getGlobalLocaleStrings()?.product_item || {};
          this.productInfo = window._erj_?.getProductInfo?.(productCard);
          this.switcherContainer = productCard.querySelector(".variant-switcher-container");
          this.inlineSwatchBlock = productCard.querySelector(".inline-swatch-picker");
          this.modals = [];
          this.selectedOptions = {};
          this.successCallback = successCallback;
          this.mainBtn = add_btn || productCard.querySelector(".hvg-product-item-button");

          

          if (!this.productInfo || !this.switcherContainer) return;

          if (this.inlineSwatchBlock) {
               // Preselect the first swatch by default
               
               const firstBtn = this.skipUnavailable ?
                    this.inlineSwatchBlock.querySelector("button[data-option]:not(.swatch-unavailable)") || this.inlineSwatchBlock.querySelector("button[data-option]")
                    :this.inlineSwatchBlock.querySelector("button[data-option]");
               if (firstBtn) {
                    firstBtn.classList.add("active");
                    this.selectedOptions[firstBtn.dataset.option] = {
                         id: firstBtn.dataset.id,
                         name: firstBtn.dataset.value,
                    };
                    this.updateMainBtnBy(firstBtn);
                    this.updateImagesAndPrice();
               }
               this.attachHandlersInline();
          }
          this.noOptions = Array.isArray(this.productInfo?.variants) && this.productInfo.variants.length === 1;
          if (this.noOptions) {
               this.selectedVariant = this.productInfo.variants[0] || null;
          }

          //  add to cart btn or buy now btn, which needs variant pick steps
          const btn = add_btn || productCard.querySelector(".hvg-product-item-button");
          if (btn) {
               btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    this.handleAddToCartClick();
               });
          }

          productCard.querySelectorAll(".inline-swatch-picker button").forEach((btn) => {
               btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
               });
          });

          ProgressiveVariantPicker.INSTANCES.push(this);
     }

     updateMainBtnBy(btn) {
          if (!btn || !this.mainBtn) return;
          if (btn.classList.contains("swatch-unavailable")) {
               this.mainBtn.innerHTML = this.localeData?.notify_button;
          } else {
               this.mainBtn.innerHTML = this.localeData?.add;
          }
     }

     // Show graphics picker (step 0) when the first option contains swatches and has many values
     requiresStepZeroModal() {
          const firstOption = this.productInfo.options?.[0];
          const hasGraphics = this.hasGraphics(firstOption);
          const overLimit = firstOption?.values && firstOption.values.length > 4;
          return Boolean(hasGraphics && overLimit && this.inlineSwatchBlock);
     }
     
     // Register inline swatch click handler
     attachHandlersInline() {
          const firstOption = this.productInfo.options?.[0];

          this.inlineSwatchBlock.querySelectorAll("button[data-option]").forEach((btn) => {
               btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    this.inlineSwatchBlock
                         .querySelectorAll("button[data-option]")
                         .forEach((b) => b.classList.remove("active"));
                    btn.classList.add("active");

                    this.selectedOptions[btn.dataset.option] = { id: btn.dataset.id, name: btn.dataset.value };

                    this.modals.forEach((m) => m && m.remove());
                    this.modals = [];

                    this.findMatchingVariant();
                    this.updateMainBtnBy(btn);
                    this.updateImagesAndPrice();
                    // No modal opening here — steps are only opened by Add to Cart (when needed) or (+) More
               });
          });

          const moreBtn = this.inlineSwatchBlock.querySelector(".inline-swatch-more");
          if (moreBtn) {
               moreBtn.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.renderStep(0, true);
               });
          }
     }

     /**
      * Render a specific picking step. Step 0 is the graphics (swatch) step when applicable.
      * If inline swatches are present and a step-0 modal is not required, it will not open.
      * @param {number} stepIdx
      * @param {boolean} [forceModal=false] Force opening the modal even when inline swatches exist
      */
     renderStep(stepIdx, forceModal = false) {
          if (this.productInfo.options.length === 1 && this.productInfo.options[0].name === "Title") return;
          if (stepIdx === 0 && this.inlineSwatchBlock && !forceModal && !this.requiresStepZeroModal()) return;

          const option = this.productInfo.options[stepIdx];
          if (!option) return;
          this.showModalForStep(option, stepIdx);
     }

     /**
      * Open modal for a given option step and attach handlers.
      * Handles placement to avoid CSS conflicts with transformed ancestors.
      * @param {Object} option
      * @param {number} stepIdx
      */
     showModalForStep(option, stepIdx) {
          ProgressiveVariantPicker.closeAllModals(this);
          this.modals.slice(stepIdx).forEach((m) => m && m.remove());
          this.modals = this.modals.slice(0, stepIdx);

          let totalSteps = this.productInfo.options.length;
          if (this.inlineSwatchBlock) totalSteps -= 1;
          let stepNumber = this.inlineSwatchBlock ? stepIdx : stepIdx + 1;

          let showBack = (stepIdx > 1 && this.inlineSwatchBlock && !this.requiresStepZeroModal()) || (this.requiresStepZeroModal() && stepIdx >0) || (!this.inlineSwatchBlock && stepIdx > 0);

          let modal = document.createElement("div");
          modal.className = "product-size-picker-modal-bg";
          modal.innerHTML = `
            <div class="product-size-picker">
               <div class="product-size-picker-header">
                    <div class="modal-back-btn" style="cursor:pointer;" data-prev="${stepIdx - 1}">
                         ${showBack ? 
                              ` <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                   <path d="M0.717157 2.71716C0.560948 2.87337 0.560948 3.12663 0.717157 3.28284L3.26274 5.82843C3.41895 5.98464 3.67222 5.98464 3.82843 5.82843C3.98464 5.67222 3.98464 5.41895 3.82843 5.26274L1.56569 3L3.82843 0.737258C3.98464 0.581049 3.98464 0.327783 3.82843 0.171573C3.67222 0.0153631 3.41895 0.0153631 3.26274 0.171573L0.717157 2.71716ZM16 3V2.6L1 2.6V3V3.4L16 3.4V3Z" fill="#666666"/>
                              </svg>`
                              : ""
                         }
                     </div>
                    <div class="product-size-picker-header-title">${this.localeData.choose} ${option.name}</div>
                    <div class="product-size-picker-close" style="cursor:pointer;"></div>
               </div>
               <div class="product-size-picker-grid">
                    ${this.renderOptionBlock(option, stepIdx)}
               </div>
            </div>
        `;

          // Fix style conflicts: if an ancestor has transform, use body for mobile viewports
          const parentHasTransform = ProgressiveVariantPicker.hasTransformAncestor(this.switcherContainer);
          const isMobile = parentHasTransform ? ProgressiveVariantPicker.isMobileViewport() : false;
          if (isMobile && parentHasTransform){
               document.body.appendChild(modal);
          } else {
               this.switcherContainer.appendChild(modal);
          }

          this.modals[stepIdx] = modal;

          modal.querySelector(".product-size-picker-close").onclick = (e) => {
               e.preventDefault();
               this.clearModalsAndSelections();
          };

          modal.addEventListener( 'click',(e) => {
               if (e.target === modal) {
                    e.preventDefault();
                    this.clearModalsAndSelections();
               }
          });
          const backBtn = modal.querySelector(".modal-back-btn");
          if (backBtn) {
               backBtn.onclick = (e) => {
                    e.preventDefault();
                    modal.remove();
                    this.modals[stepIdx] = null;
                    const prevStepIdx = stepIdx - 1;
                    if (prevStepIdx === 0 && this.inlineSwatchBlock && !this.requiresStepZeroModal()) {
                         // Just clear modals and let the inline swatch stay selected
                         return;
                    }

                    this.renderStep(prevStepIdx, true);
               };
          }
          this.attachHandlers(stepIdx, modal);

          this.preventDefaultNav();

          // const grid = modal.querySelector('.product-size-picker-grid');
          // const scrollHint = grid.querySelector('.scroll-hint');

          // grid.addEventListener('scroll', () => {
          //      const fullyScrolled = grid.scrollTop + grid.clientHeight >= grid.scrollHeight - 1;
          //      if (scrollHint) scrollHint.style.display = fullyScrolled ? 'none' : 'flex';
          // });

          // setTimeout(() => {
          //      const fullyScrolled = grid.scrollTop + grid.clientHeight >= grid.scrollHeight - 1;
          //      if (scrollHint) scrollHint.style.display = fullyScrolled ? 'none' : 'flex';
          // }, 50);
     }

     /**
      * Register click handlers for option buttons inside a modal.
      * Advances steps progressively and auto-adds to cart when all options are chosen.
      * @param {number} stepIdx
      * @param {HTMLElement|null} [container=null] Modal root to scope query; defaults to current step modal
      */
     attachHandlers(stepIdx, container = null) {
          const root = container || this.modals[stepIdx];
          root.querySelectorAll("button[data-option]").forEach((btn) => {
               btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    root.querySelectorAll("button[data-option]").forEach((b) => b.classList.remove("active"));
                    btn.classList.add("active");

                    this.selectedOptions[btn.dataset.option] = { id: btn.dataset.id, name: btn.dataset.value };

                    const options = this.productInfo.options.map((opt) => opt.name);
                    options.slice(stepIdx + 1).forEach((optName) => delete this.selectedOptions[optName]);
                    this.modals.slice(stepIdx + 1).forEach((m) => m && m.remove());
                    this.modals = this.modals.slice(0, stepIdx + 1);

                    this.updateImagesAndPrice();

                    const allPicked = this.productInfo.options.every((opt) => this.selectedOptions[opt.name]);
                    const variant = this.findMatchingVariant();
                    if (allPicked) {
                         if (variant && variant.available) {
                              this.modals.forEach((m) => m && m.remove());
                              this.resetDefaultNav();
                              this.modals = [];
                              if (this.successCallback) {
                                   this.successCallback(variant);
                              }else{
                                   this.addToCart(variant);
                              }
                         } else {
                              this.showBackInStockModal(stepIdx);
                         }
                    } else if (stepIdx + 1 < this.productInfo.options.length && container) {
                         this.renderStep(stepIdx + 1, true);
                    }
               });
          });
     }

     attachBackInStockHandler(modal, stepIdx) {
          const form = modal.querySelector(".back-in-stock-form");
          const submit = form?.querySelector(".back-in-stock-submit");
          if (submit) {
               submit.onclick = () => {
                    const email = form.querySelector(".back-in-stock-email").value.trim();
                    if (email.length > 0 && /\S+@\S+\.\S+/.test(email)) {
                         // Simulate form submit (replace with your action)
                         console.log("Back in stock notification submitted:", email, this.selectedOptions);
                         form.innerHTML = `<div class="back-in-stock-success">${this.localeData.thank_you_notify}</div>`;
                    }
               };
          }
     }

     /**
      * Whether the option contains at least one value with a swatch (color or image)
      * @param {Object} option
      * @returns {boolean}
      */
     hasGraphics(option) {
          return !!(option?.values?.some((v) => v.swatch && (v.swatch.color || v.swatch.image)));
     }
     findMatchingVariant() {
          if (this.noOptions) {
               return this.productInfo?.variants?.[0] || null;
          }
          const selectedArr = this.productInfo.options.map((opt) => {
               const sel = this.selectedOptions[opt.name];
               return sel ? String(sel.id) : null;
          });
          return this.productInfo.variants.find((variant) => {
               return variant.options.every((optVal, idx) => {
                    const valObj = this.productInfo.options[idx].values.find((v) => v.name === optVal);
                    if (!valObj) return false;
                    if (!selectedArr[idx]) return true;
                    return String(valObj.id) === selectedArr[idx];
               });
          });
     }

     /**
      * Compute possible and available values for the current option step,
      * given previously selected option names.
      * @param {Object} option
      * @param {number} stepIdx
      * @returns {Array<{name:string,id:string|number,swatch?:Object,possible:boolean,available:boolean}>}
      */
     getPossibleValues(option, stepIdx) {
          const opts = this.productInfo.options;

          // Names of earlier selections (null if not yet picked)
          const prevNames = opts.slice(0, stepIdx).map((opt) => this.selectedOptions[opt.name]?.name || null);

          // Keep only the variants that match all earlier selections
          const candidates = this.productInfo.variants.filter((v) =>
               prevNames.every((sel, i) => !sel || v.options[i] === sel)
          );

          // Build quick lookup for this step’s possible/available values
          const byName = new Map();
          option.values.forEach((val) => byName.set(val.name, { possible: false, available: false }));

          for (const v of candidates) {
               const valName = v.options[stepIdx];
               if (byName.has(valName)) {
                    const entry = byName.get(valName);
                    entry.possible = true;
                    if (v.available) entry.available = true;
               }
          }

          return option.values.map((val) => ({
               name: val.name,
               id: val.id,
               swatch: val.swatch ?? null,
               possible: byName.get(val.name)?.possible || false,
               available: byName.get(val.name)?.available || false,
          }));
     }

     /**
      * Update main/secondary images, price and shipping snippets based on the currently matched variant.
      */
     updateImagesAndPrice() {
          const match = this.findMatchingVariant();
          if (!match) return;
          const productCard = this.switcherContainer.closest(".product-item");
          if (!productCard) return;
          const mainImg = productCard.querySelector("img.imagemain");
          if (mainImg && match.image && mainImg.src !== match.image) {
               mainImg.src = decorateImgUrl(match.image, 600)
          }
          const secondImg = productCard.querySelector("img.second_image");
          if (secondImg && match.variantModelImage && secondImg.src !== match.variantModelImage) {
               secondImg.src = decorateImgUrl(match.variantModelImage, 600);
          }
          const priceDiv = productCard.querySelector("div.price");
          if (priceDiv) {
               if (match.off && match.off.length > 0) {
                    priceDiv.innerHTML = `
          <div class="pricehot">
          <span class="hot"><span class="money">${Shopify.formatMoney(match.price)}</span></span>
          <span class="hot"> ${match.off}</span>
          </div>
          <span class="old money">${Shopify.formatMoney(match.compare_at_price)}</span>`;
               } else {
                    priceDiv.innerHTML = Shopify.formatMoney(match.price);
               }
          }
          const shippingDiv = productCard.querySelector("div.hvg-shipping-info-product-item");
          if (shippingDiv) {
               if (match.variantShippingText && match.variantShippingText.length > 0) {
                    shippingDiv.innerHTML = match.variantShippingText;
               } else if (this.productInfo.shippingText) {
                    shippingDiv.innerHTML = this.productInfo.shippingText;
               }
          }
          const swiperContainer = productCard.querySelector(".product-swiper-container");
          if (swiperContainer) {
               swiperContainer.scrollTo(0, 0);
          }


     }

     /**
      * Render and display the Back In Stock modal for unavailable variant selections.
      * @param {number} lastStepIdx
      */
     showBackInStockModal(lastStepIdx) {
          ProgressiveVariantPicker.closeAllModals(this);
          const variant = this.findMatchingVariant();
          new BackInStockModal(variant,this.productCard,this.productInfo,this.clearModalsAndSelections.bind(this))
     }

     /**
      * Render an option block (swatches, numeric list, or text list) for a given step.
      * @param {Object} option
      * @param {number} stepIdx
      * @returns {string} HTML string
      */
     renderOptionBlock(option, stepIdx) {
          const allValues = this.getPossibleValues(option, stepIdx);
          let valueObjs = (stepIdx > 0)
          ? allValues.filter(v => v.possible)
          : allValues;

          const isSwatch = valueObjs.some((v) => v.swatch && (v.swatch.color || v.swatch.image));
          // render Numeric List if all values are numbers and less than or equal to 8
          const isNumericList = valueObjs.every((v) => /^\d+$/.test(v.name)) && valueObjs.length <= 8;
          let html = `<div class="switcher-block" data-option="${option.name}">`;

          const selectedObj = this.selectedOptions[option.name];
          const selectedId = selectedObj ? String(selectedObj.id) : null;

          if (isSwatch) {
               html += `<div class="picker-grid swatch-list">`;
               html += valueObjs
                    .map((val) => this.renderSwatchValue(option, val, String(val.id) === selectedId))
                    .join("");
               html += `</div>`;
          } else if (isNumericList) {
               html += `<div class="picker-grid size-grid">`;
               html += valueObjs
                    .map((val) => this.renderSizeValue(option, val, String(val.id) === selectedId))
                    .join("");
               html += `</div>`;
          } else {
               html += `<div class="picker-grid text-grid">`;
               html += valueObjs
                    .map((val) => this.renderTextValue(option, val, String(val.id) === selectedId))
                    .join("");
               html += `</div>`;
          }

          html += `</div>`;
          return html;
     }
     clearModalsAndSelections() {
          this.modals.forEach((m) => m && m.remove());
          this.resetDefaultNav();
          this.modals = [];
          const prevSelectedOptions = this.selectedOptions;
          this.selectedOptions = {};

          if (this.inlineSwatchBlock) {
               const firstBtn = this.skipUnavailable ?
                    this.inlineSwatchBlock.querySelector("button[data-option]:not(.swatch-unavailable)") || this.inlineSwatchBlock.querySelector("button[data-option]")
                    :this.inlineSwatchBlock.querySelector("button[data-option]");
               if (firstBtn) {
                    if (prevSelectedOptions[firstBtn.dataset.option]){
                         this.selectedOptions[firstBtn.dataset.option] = prevSelectedOptions[firstBtn.dataset.option];
                    } else{
                         this.inlineSwatchBlock
                              .querySelectorAll("button[data-option]")
                              .forEach((b) => b.classList.remove("active"));
                         firstBtn.classList.add("active");
                         this.selectedOptions[firstBtn.dataset.option] = {
                              id: firstBtn.dataset.id,
                              name: firstBtn.dataset.value,
                         };
                    }    
               }
          }
          this.updateImagesAndPrice();
     }

     static addToCartAsync(id, productId, quantity, handle, properties) {
          const appElementEn = document.querySelector("#app");
          const appEn = appElementEn?.__vue__;
          if (appEn) {
               appEn.addToCart({ id, productId, quantity, handle, properties });
          }
     }
     static closeAllModals(skipInstance) {
          document.querySelectorAll(".product-size-picker-modal-bg").forEach((modalBg) => {
               modalBg.remove();
          });
          ProgressiveVariantPicker.INSTANCES.forEach((instance) => {
               if (instance !== skipInstance) {
                    instance.clearModalsAndSelections();
               }
          });
     }

     /**
      * Add the chosen variant to cart and clean up UI state.
      * @param {Object} variant
      */
     addToCart(variant) {
          if (!variant) return;
          ProgressiveVariantPicker.addToCartAsync(
               variant.id,
               this.productInfo.productId,
               1,
               this.productInfo.handle,
               {}
          );
          this.refreshSelectedOption();
          if (this.switcherContainer)
               this.switcherContainer.querySelectorAll(".active").forEach((btn) => btn.classList.remove("active"));
          this.modals.forEach((m) => m && m.remove());
          this.modals = [];
          this.resetDefaultNav();
          this.updateImagesAndPrice();
          const firstOption = this.productInfo.options[0];
          if (this.hasGraphics(firstOption) && this.inlineSwatchBlock) {
               this.renderStep(0);
          }
     }
     /**
      * Sync selectedOptions from the inline swatch block.
      * Used after cart actions to keep UI and price/images in sync.
      */
     refreshSelectedOption(){
          this.selectedOptions = {};
          if (this.inlineSwatchBlock) {
               const selectedBtn = this.inlineSwatchBlock.querySelector("button[data-option].active");
               if (selectedBtn) {
                    this.selectedOptions[selectedBtn.dataset.option] = {
                         id: selectedBtn.dataset.id,
                         name: selectedBtn.dataset.value,
                    };
                    this.updateImagesAndPrice();
               }
          }
     }

     /**
      * Entry point for Add-to-Cart button.
      * Opens picking flow only when needed; otherwise adds directly.
      */
     handleAddToCartClick() {
          if (this.noOptions) {
               const v = this.selectedVariant || this.findMatchingVariant();
               if (v) {
                    this.selectedVariant = v;

                    if (v.available) {
                         if (this.successCallback) {
                              this.successCallback(v);
                         }else{
                              this.addToCart(v);
                         }
                    } else {
                         this.showBackInStockModal(0);
                    }
               }
               return;
          }
          const allPicked = this.productInfo.options.every((opt) => this.selectedOptions[opt.name]);
          const variant = this.findMatchingVariant();
          const onlyOneOption = this.productInfo.options.length === 1;
          const skipModal = onlyOneOption && !this.requiresStepZeroModal();

          if (!allPicked) {
               if (this.inlineSwatchBlock && !this.requiresStepZeroModal() && this.productInfo.options.length > 1) {
                    this.renderStep(1, true);
               } else {
                    this.renderStep(0, true);
               }
               return;
          }

          if (variant) {
               if (variant.available) {
                    if (skipModal) {
                         if (this.successCallback) {
                              this.successCallback(variant);
                         }else{
                              this.addToCart(variant);
                         }
                    } else if (
                         this.inlineSwatchBlock &&
                         !this.requiresStepZeroModal() &&
                         this.productInfo.options.length > 1
                    ) {
                         this.renderStep(1, true);
                    } else {
                         this.renderStep(0, true);
                    }
               } else {
                    this.showBackInStockModal(0);
               }
          }
     }

     renderSwatchValue(option, val, isActive = false) {
          return `
               <button class="swatch-list-btn${!val.available ? " swatch-unavailable" : ""}${
               !val.possible ? " swatch-not-possible" : ""
          }${isActive ? " active" : ""}"
                         type="button"
                         data-option="${option.name}"
                         data-value="${val.name}"
                         data-id="${val.id}"
                         data-available="${val.available}">
                    <span class="swatch-icon">
                         ${
                              val.swatch?.image
                                   ? `<img src="${val.swatch.image}" alt="${val.name}" class="swatch-circle" />`
                                   : val.swatch?.color
                                   ? `<span class="swatch-circle" style="background-color: ${val.swatch.color};"></span>`
                                   : ""
                         }
                    </span>
                    <span class="swatch-label"><span>${val.name}</span></span>
                    ${
                         !val.available
                              ? `<span class="unavailable-bell" aria-label="Unavailable option"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg></span>`
                              : ""
                    }
               </button>
          `;
     }

     renderSizeValue(option, val, isActive = false) {
          return `
               <button class="product-size-picker-item size-btn${!val.available ? " swatch-unavailable" : ""}${
               !val.possible ? " swatch-not-possible" : ""
          }${isActive ? " active" : ""}"
                         type="button"
                         data-option="${option.name}"
                         data-value="${val.name}"
                         data-id="${val.id}"
                         data-available="${val.available}">
                    ${
                         !val.available
                              ? `<span class="unavailable-bell" aria-label="Unavailable option"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg></span>`
                              : ""
                    }
                    <span class="product-size-picker-item-label">${val.name}</span>
               </button>`;
     }

     renderTextValue(option, val, isActive = false) {
          return `
          <button class="product-size-picker-item list-btn${!val.available ? " swatch-unavailable" : ""}${
               !val.possible ? " swatch-not-possible" : ""
          }${isActive ? " active" : ""}"
                    type="button"
                    data-option="${option.name}"
                    data-value="${val.name}"
                    data-id="${val.id}"
                    data-available="${val.available}">
               ${
                    !val.available
                         ? `<span class="unavailable-bell" aria-label="Unavailable option"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg></span>`
                         : ""
               }
               <span class="product-size-picker-item-label">${val.name}</span>
          </button>`;
     }

     /**
      * Prevent wrapping product links from navigating while the modal is open.
      */
     preventDefaultNav() {
          const wrapLink =
               this.productCard.closest("a[href]") ||
               this.productCard.querySelector(":scope > a[href]");
          if (wrapLink) {
               this.wrapLink = wrapLink;
               this.savedHref = wrapLink.getAttribute("href");
               wrapLink.removeAttribute("href");
          }
     }

     /**
      * Restore default navigation on wrapping product links.
      */
     resetDefaultNav() {
          if (this.wrapLink && this.savedHref)
               this.wrapLink.setAttribute("href", this.savedHref);
     }
}

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".product-item").forEach((card) => {
    if (card.dataset.variantPickerInitialized === "true") return;

    new ProgressiveVariantPicker(card,null,null,false);
    card.dataset.variantPickerInitialized = "true";

    card.addEventListener("click", function (e) {
      if (
        e.target.closest("button") ||
        e.target.closest(".inline-swatch-picker") ||
        e.target.closest(".hvg-product-item-button")
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });
  });
});

function initVariantPickersInNewItems(root = document) {
  root.querySelectorAll(".product-item").forEach((card) => {
    if (card.dataset.variantPickerInitialized === "true") return;

    new ProgressiveVariantPicker(card,null,null,false);
    card.dataset.variantPickerInitialized = "true";
  });
}
